<template>
  <div class="card">
    <div class="card-header pb-0 d-flex justify-content-between align-items-center">
      <div>
        <h6>挑战订单明细</h6>
        <p class="text-xs text-secondary">逐笔挑战订单，含挑战结果与出货结果</p>
      </div>
      <button class="btn btn-sm btn-outline-info" @click="fetchData"><i class="ni ni-refresh"></i> 刷新</button>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="loading" class="text-center text-secondary text-xs py-4">加载中...</div>
      <div v-else-if="records.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
      <div v-else>
        <!-- 汇总统计 -->
        <div class="d-flex flex-wrap gap-3 px-3 pb-2">
          <span class="badge bg-info">总笔数: {{ records.length }}</span>
          <span class="badge bg-success">挑战成功: {{ winCount }}</span>
          <span class="badge bg-danger">挑战失败: {{ loseCount }}</span>
          <span class="badge bg-warning text-dark">保底赠送: {{ guaranteeCount }}</span>
        </div>
        <!-- 明细列表 -->
        <div class="table-responsive p-0">
          <table class="table table-sm align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-xs text-secondary">订单号</th>
                <th class="text-xs text-secondary">设备ID</th>
                <th class="text-xs text-secondary">货道</th>
                <th class="text-xs text-secondary text-end">金额</th>
                <th class="text-xs text-secondary text-center">挑战结果</th>
                <th class="text-xs text-secondary text-center">出货结果</th>
                <th class="text-xs text-secondary text-center">补偿</th>
                <th class="text-xs text-secondary text-end">时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in displayRecords" :key="item.order_no">
                <td class="text-xs font-monospace">{{ item.order_no }}</td>
                <td class="text-xs font-monospace">{{ item.board_id }}</td>
                <td class="text-xs">{{ item.channel_idx }}</td>
                <td class="text-xs text-end">¥{{ formatMoney(item.fee) }}</td>
                <td class="text-center">
                  <span :class="challengeResultClass(item)" class="badge text-xs">{{ challengeResultText(item) }}</span>
                </td>
                <td class="text-center">
                  <span :class="deliverResultClass(item)" class="badge text-xs">{{ deliverResultText(item) }}</span>
                </td>
                <td class="text-center">
                  <span v-if="item.comp_type" class="badge bg-warning text-dark text-xs">{{ item.comp_type }}</span>
                  <span v-else class="text-xs text-secondary">-</span>
                </td>
                <td class="text-xs text-end">{{ formatDate(item.created_date) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- 分页 -->
        <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
          <nav>
            <ul class="pagination pagination-sm">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="javascript:;" @click="currentPage--">上一页</a>
              </li>
              <li class="page-item active"><span class="page-link">{{ currentPage }}/{{ totalPages }}</span></li>
              <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                <a class="page-link" href="javascript:;" @click="currentPage++">下一页</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getChallengeOrders } from "@/api/dashboard";

export default {
  name: "ChallengeOrderReport",
  props: {
    deliverId: { type: Number, default: 0 },
    startDate: { type: Number, required: true },
    endDate: { type: Number, required: true },
  },
  data() {
    return {
      records: [],
      loading: false,
      currentPage: 1,
      pageSize: 15,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.records.length / this.pageSize);
    },
    displayRecords() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.records.slice(start, start + this.pageSize);
    },
    winCount() {
      return this.records.filter((r) => r.challenge_result === 1).length;
    },
    loseCount() {
      return this.records.filter((r) => r.challenge_result === 0).length;
    },
    guaranteeCount() {
      return this.records.filter((r) => r.challenge_result === 2).length;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatMoney(fen) {
      if (!fen) return "0.00";
      return (fen / 100).toFixed(2);
    },
    formatDate(ts) {
      if (!ts) return "-";
      const d = new Date(ts * 1000);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    challengeResultText(item) {
      if (item.challenge_result === 1) return "挑战成功";
      if (item.challenge_result === 0) return "挑战失败";
      if (item.challenge_result === 2) return "保底赠送";
      return "未知";
    },
    challengeResultClass(item) {
      if (item.challenge_result === 1) return "bg-success";
      if (item.challenge_result === 0) return "bg-danger";
      if (item.challenge_result === 2) return "bg-warning text-dark";
      return "bg-secondary";
    },
    deliverResultText(item) {
      if (item.exec_result === 0) return "出货成功";
      if (item.exec_result === null || item.exec_result === undefined) return "待处理";
      return "出货失败";
    },
    deliverResultClass(item) {
      if (item.exec_result === 0) return "bg-success";
      if (item.exec_result === null || item.exec_result === undefined) return "bg-warning text-dark";
      return "bg-danger";
    },
    async fetchData() {
      this.loading = true;
      this.currentPage = 1;
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate,
          page: 1,
          page_size: 10000,
        };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getChallengeOrders(params);
        if (resp.code === 0 && resp.desc) {
          let records = [];
          if (Array.isArray(resp.desc)) {
            records = resp.desc;
          } else if (resp.desc.records) {
            records = resp.desc.records;
          }
          this.records = records;
        }
      } catch (err) {
        console.error("获取挑战订单明细失败:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
